/**
 * Product journey step and deliverable metadata used by the canvas and AI flows.
 * Defines step IDs, prompt keys, deliverable IDs, and step ordering for the guided flow.
 *
 * Steps are DECOUPLED (LEV-1172): any step can be created at any time, in any
 * order. `STEP_ORDER` is the recommended default path from zero, and
 * `typicallyAfter` records soft dependencies — both are informational only and
 * MUST NOT be used to gate creation or generation.
 */
export const STEP_IDS = {
  // NOTE: Step IDs are persisted in the database; do not rename without a migration.
  // The initial idea step is stored as "solution-overview" in existing projects.
  IDEA: "solution-overview",
  CUSTOMER_PROBLEM: "customer-problem",
  COMPETITIVE: "competitive",
  TAM: "tam",
  /**
   * BRAND — the Brand Development step.
   *
   * The stored value is the LEGACY step id "positioning", kept intentionally.
   * DB rows, events, analytics, and historical payloads all say "positioning"
   * forever. This is a code-side-only rename (Option A) decided in
   * docs/plans/2026-07-27-lev-1243-1xux-implementation-plan.md section 4.4.
   *
   * For any code that reasons about raw stored values (analytics queries,
   * event consumers, scripts), use LEGACY_STEP_ID_VALUES.brand instead of a
   * magic string.
   */
  BRAND: "positioning",
  GTM: "gtm",
  PRODUCT_BUILD: "product-build",
  TEAM: "team",
  /**
   * FUNDRAISING — the raise lane. THIS is the fundraising row.
   *
   * The stored value reads `investor-studio` because that was this rendering's
   * internal codename. The stored value stays as it is, exactly
   * like BRAND → "positioning" above, because renaming a stored value buys
   * nothing and costs a data migration plus every PostHog series keyed on it.
   * `LEGACY_STEP_ID_VALUES.fundraising` states the mapping in one place.
   *
   * "Investor Studio" was never founder-facing — the label has always been
   * "Fundraising" — but the codename leaked far enough through the code and
   * docs to make people ask which of the two was the real feature, which is
   * why it is gone from the key names.
   *
   * Included in Create All and STEP_ORDER. Gated by its own feature flag.
   */
  FUNDRAISING: "investor-studio",
} as const;

/**
 * Explicit mapping of renamed constant keys to their legacy stored values.
 * Use this whenever code must match raw DB/event/analytics values by the
 * new constant name. One named source of truth -- no magic strings.
 *
 * Decided: docs/plans/2026-07-27-lev-1243-1xux-implementation-plan.md section 4.4 (Option A).
 */
export const LEGACY_STEP_ID_VALUES = {
  brand: "positioning",
  fundraising: "investor-studio",
} as const;

export type StepId = (typeof STEP_IDS)[keyof typeof STEP_IDS];

export const PROMPT_KEYS = {
  IDEA_STATEMENT: "ideaStatement",
  CUSTOMER_PROBLEM: "customerProblem",
  INTERVIEW_SCRIPT: "interviewScript",
  COLD_OUTREACH: "coldOutreach",
  // PROSPECT_LIST_20: "prospectList20",
  COMPETITIVE_LANDSCAPE: "competitiveLandscape",
  COMPETITORS_DATA: "competitorsData",
  TAM_ANALYSIS: "tamAnalysis",
  MARKET_SIZING: "marketSizing",
  BRAND_POSITIONING: "brandPositioning",
  BRAND_VISUALS: "brandVisuals",
  GO_TO_MARKET: "goToMarket",
  GTM_EXPERIMENTS: "gtmExperiments",
  PROSPECT_LIST: "prospectList",
  LANDING_PAGE: "landingPage",
  PRODUCT_SPEC: "productSpec",
  PRD: "prd",
  TRD: "trd",
  TECHNICAL_SPEC: "technicalSpec",
  USER_STORIES: "userStories",
  FINAL_DOSSIER: "finalDossier",
  TEAM_ANALYSIS: "teamAnalysis",
  TEAM_JOB_DESCRIPTIONS: "teamJobDescriptions",
  SUMMARY: "summary",
  // Fundraising row. Prompt bodies land with generation in Stage 2 — these
  // keys are metadata today (nothing resolves a prompt from `promptName`;
  // generation is keyed by deliverable id in src/lib/ai/deliverables.ts).
  RAISE_BRIEF: "raiseBrief",
  FUNDING_CLIMATE: "fundingClimate",
  PITCH_DECK: "pitchDeck",
} as const;

export type PromptKey = (typeof PROMPT_KEYS)[keyof typeof PROMPT_KEYS];

export const DELIVERABLE_IDS = {
  INTERVIEW_SCRIPT: "interview-script",
  COLD_OUTREACH: "cold-outreach",
  // Tile 02 of the v2 Customer Discovery row (row-order spec D3). Lives in
  // CUSTOMER_DISCOVERY_V2_NODES below, deliberately NOT in PRODUCT_STEPS.
  ICP_CRITERIA: "icp-criteria",
  // PROSPECT_LIST retained for back-compat with existing rows / Task 11 prospect-router cleanup.
  // New pipeline-board deliverables use DISCOVERY/APPLICANTS instead.
  PROSPECT_LIST: "prospect-list",
  DISCOVERY: "discovery",
  APPLICANTS: "applicants",
  COMPETITORS_CSV: "competitors-csv",
  MARKET_SIZING: "market-sizing",
  BRAND_VISUALS: "brand-visuals",
  EXPERIMENTS: "experiments",
  LANDING_PAGE: "landing-page",
  PRD: "prd",
  TRD: "trd",
  FINAL_DOSSIER: "final-dossier",
  USER_STORIES: "user-stories",
  TECHNICAL_ARCHITECTURE: "technical-architecture",
  TEAM_JOB_DESCRIPTIONS: "team-job-descriptions",
  SUMMARY: "summary",
  // Fundraising row, split by JOB rather than by artifact (2026-08-15). The
  // founder's framing: research the landscape, work on the materials, build a
  // target list, engage and track. That is how a founder describes their own
  // week, so it is the right vocabulary for the UI — and a job-shaped block is
  // the right unit to later mount publicly as a standalone founder tool, which
  // an eight-stage mega-block is not.
  //
  // Horizontal = these four blocks across the canvas. Vertical = the stage
  // rail inside each one. A founder can work either way round.
  STUDIO_PATH: "studio-path",
  STUDIO_RESEARCH: "studio-research",
  STUDIO_MATERIALS: "studio-materials",
  STUDIO_LIST: "studio-list",
  STUDIO_TARGETS: "studio-targets",
  // API_SCHEMAS: "api-schemas",
  // DATABASE_SCHEMA: "database-schema",
  // AI_AGENT_TASK_LIST: "ai-agent-task-list",
} as const;

export type DeliverableId = (typeof DELIVERABLE_IDS)[keyof typeof DELIVERABLE_IDS];

/**
 * Minimum number of Discovery `PipelineEntry` rows below which "Ask Lev to
 * complete" on a Category B Discovery task dispatches the Apollo prospect
 * generator instead of just opening + completing the deliverable (LEV-1065).
 * At/above this count the founder already has enough prospects, so Lev skips
 * Apollo and behaves like the default Category B branch. v1 = 5.
 */
export const APOLLO_MIN_ROWS = 5;

export interface DeliverablePreviewPalette {
  headerColor: string;
  surfaceColor: string;
  accentColor: string;
}

/**
 * Typed fact keys for the generation autonomy decision tree (ADR 033).
 * Canonical definition lives in src/constants/facts.ts (proto-blocks module 1).
 * Re-exported here so existing neededFacts callers keep compiling without changes.
 */
import type { FactKey } from "@/constants/facts";
export type { FactKey };

export type ContentType = "markdown" | "csv" | "html";
export type DeliverableDisplayType =
  | "text"
  | "csv"
  | "html"
  | "prospect_list"
  | "brand_visuals"
  | "pipeline_board";

export interface DeliverableNodeDefinition {
  id: DeliverableId;
  title: string;
  promptName: PromptKey;
  icon: string;
  previewPalette?: DeliverablePreviewPalette;
  /** The format of the generated content (markdown, csv, html) */
  contentType?: ContentType;
  /** Optional custom viewer component to use instead of default format renderer */
  customViewer?: string;
  requiredInputs?: StepId[];
  generationSource?: "llm" | "apollo";
  /** Whether this deliverable supports download action. Defaults to true for standard content types. */
  downloadable?: boolean;
  /** Whether this deliverable supports copy action. Defaults to true. */
  copyable?: boolean;
  /** Used by ContentRenderer to determine the appropriate CustomViewer for the deliverable. */
  displayType?: DeliverableDisplayType;
  /**
   * Whether completing this deliverable gates task generation in the v3
   * generator. Document-style deliverables (markdown/csv/html) gate (`true`);
   * pipeline-board deliverables, which are people lists rather than founder
   * work products, do not (`false`). Set explicitly on every deliverable — do
   * not rely on a default.
   */
  gatesTaskGeneration: boolean;
  /**
   * Facts a high-quality generation of this deliverable requires (ADR 033).
   * Used by the sufficiency judgment to decide auto-generation eligibility.
   * Tunable constant -- editing this list tunes autonomy behavior.
   */
  neededFacts?: FactKey[];
  /**
   * Whether a DRAFT of this deliverable needs founder review + approval.
   * Defaults to true (reviewed) when omitted. Set to false for derived
   * artifacts like the Summary — a recap of already-approved steps, not a
   * work product in its own right — so the impact queue never surfaces a
   * "Review X" item for it and the lightbox shows no signoff chips.
   */
  requiresReview?: boolean;
  /**
   * One founder-voice sentence on why this deliverable is worth reviewing, for
   * the NEXT ACTION block's "why it matters" line (spec 2026-09-03). Set only
   * on review-requiring deliverables; `whyItMattersFor` falls back to the
   * parent step's sentence when a deliverable omits it.
   */
  whyItMatters?: string;
  /**
   * Whether the canvas/lightbox renders this deliverable view-only (no edit
   * affordance). Defaults to false (editable). Distinct from `requiresReview`:
   * a deliverable can be reviewable yet read-only or vice versa.
   */
  readOnly?: boolean;
  /**
   * TEMPORARY: this deliverable is registered but has no generator yet.
   *
   * Registration and generation are deliberately separable for one reason —
   * `Block.title` persists per row, so a title must be fixed BEFORE the first
   * project creates the block or every existing project is stranded on the
   * old name (the BRAND/"positioning" situation above). The Fundraising row
   * registers its titles in one commit and lands generators across later
   * stages.
   *
   * This is an explicit marker rather than an implicit gap: without it, "no
   * generator" is indistinguishable from "someone forgot", which is exactly
   * how a deliverable ships as a card the founder can click and nothing
   * happens. `deliverables.registry.test.ts` skips these AND asserts the
   * marker is removed once a generator exists, so it cannot go stale.
   */
  generatorPending?: true;
  /**
   * PERMANENT: this deliverable has no document generator by construction —
   * its `customViewer` owns the content.
   *
   * Distinct from `generatorPending`, which is a temporary marker for a
   * generator that has not landed YET. A viewer-only deliverable is never
   * going to have one: the panel drives its own state through its routers,
   * and there is no document to produce. The Fundraising blocks work this
   * way, as do pipeline boards (a board is a living list of PipelineEntry
   * rows, not a document).
   *
   * Marked explicitly so "no generator" cannot be confused with "someone
   * forgot" — the same reason `generatorPending` exists. Without it, the
   * registry test either fails on a correct definition or has to grow a
   * hardcoded list of exempt ids, which silently stops covering a deliverable
   * the day someone reuses a viewer name.
   */
  viewerOnly?: true;
}

export interface Step {
  id: StepId;
  index: number;
  label: string;
  description: string;
  /**
   * One founder-voice sentence on why this step is worth doing, for the
   * "why it matters" line of the derived NEXT ACTION block (spec 2026-09-03).
   * Product-copy-voice: plain, second person, no hype. See `whyItMattersFor`.
   */
  whyItMatters: string;
  /**
   * Optional: pipeline-only lanes have no primary doc and no prompt key. All
   * current product-journey steps supply both, but the fields stay optional so
   * a future pipeline-only lane can be added without a type churn. Downstream
   * callsites must guard against undefined — see `getPrimaryTitle` for the
   * canonical access pattern.
   */
  promptName?: PromptKey;
  primaryNodeTitle?: string;
  icon: string;
  // Header gradient colors for the primary document card for this step (used in canvas UI)
  headerGradientTop: string;
  headerGradientBottom: string;
  // Solid header color for deliverable cards on the canvas for this step
  deliverableHeaderColor: string;
  /**
   * Background color of the 32×32 step-icon chip in the canvas-v2 card
   * header (rendered by `CardHeader` at `iconSize="sm"`). Intentionally a
   * *darker / more saturated* sibling of the step's `headerGradientTop`
   * (see `#fcc590` vs. `#FFD3A8` for Big Idea) so the chip reads as a
   * clear family accent against the gradient behind it rather than
   * disappearing into it. Picked by eye so the contrast delta lines up
   * roughly with Big Idea's peach pair — tweak per Figma if needed.
   */
  stepIconTileColor: string;
  deliverableNodes?: DeliverableNodeDefinition[];
  /**
   * Soft dependency DAG: the steps whose outputs this step typically builds
   * on. Informational only — used for recommendations and context framing,
   * NEVER as a gate. A step with an empty list (e.g. Team) is useful from day
   * one; a founder can create any step at any time regardless of this field.
   */
  typicallyAfter?: StepId[];
  rubricCategories?: string[];
  requiredInputs?: StepId[];
  /**
   * Facts a high-quality generation of this step's primary block requires
   * (ADR 033). Used by the sufficiency judgment to decide auto-generation
   * eligibility. Tunable constant -- editing this list tunes autonomy behavior.
   */
  neededFacts?: FactKey[];
  /**
   * Whether this step is included in the "Create All" one-click flow.
   * Defaults to true (included) when omitted.
   * Set to false for steps that require manual inputs before they can run.
   *
   * Consumer behavior differs by intent:
   * - areAllCreateAllStepsComplete (nextStep.ts): uses `continue` — skips excluded steps,
   *   still checks any included steps that follow.
   * - useCreateAll (canvas hook): for excluded steps with a primaryNodeTitle (manual-input
   *   gate), uses `break` because subsequent steps depend on the primary content. For
   *   excluded pipeline-only lanes (no primaryNodeTitle), uses `continue` — they have
   *   no primary doc to generate and no downstream steps depend on them.
   */
  includeInCreateAll?: boolean;
  /**
   * Signoff configuration (Decision 4 of the draft-signoff design). Absent
   * means generic review chips: "Looks good" (approve, primary) then "Help me
   * improve this" (improve, secondary).
   *
   * `kind: "choice"` is choice-bearing signoff: the generation emits structured
   * candidates into block metadata, the lightbox renders one chip per
   * candidate, and picking a candidate triggers a server-authoritative chain.
   *
   * `kind: "review"` carries no candidates — it only relabels the improve
   * chip (Big Idea uses "Improve") without adding a choice
   * mechanic. "Looks good" is always the primary chip and improve always
   * secondary, for both signed-in and anonymous actors (operator decision
   * 2026-09-09, LEV-1647): a primary action must never open the signup modal.
   */
  signoff?:
    | ({
        kind: "choice";
        candidatesKey: string;
        chipTemplate: string;
        rewriteOnChoice: boolean;
        chosenKey: string;
      } & ImproveLabelConfig)
    | ({ kind: "review" } & ReviewChipConfig);
}

type ImproveLabelConfig = { improveLabel?: string };
type ReviewChipConfig = ImproveLabelConfig;

const DELIVERABLE_PREVIEW_PALETTES: Record<string, DeliverablePreviewPalette> = {
  frost: {
    headerColor: "#F1F5FC",
    surfaceColor: "#E1E9F5",
    accentColor: "#B8CCE7",
  },
  mist: {
    headerColor: "#F3F7FC",
    surfaceColor: "#E4EDF7",
    accentColor: "#BBD0E9",
  },
  slate: {
    headerColor: "#EFF3F8",
    surfaceColor: "#DEE6EF",
    accentColor: "#B5C5D8",
  },
  dusk: {
    headerColor: "#F2F6FB",
    surfaceColor: "#E2ECF6",
    accentColor: "#B8C9E3",
  },
  tide: {
    headerColor: "#F5F8FB",
    surfaceColor: "#E6EEF5",
    accentColor: "#BED2E6",
  },
};

export const PRODUCT_STEPS: Step[] = [
  {
    id: STEP_IDS.IDEA,
    index: 0,
    label: "Big Idea",
    description: "What You're Building",
    whyItMatters: "your idea in one clear line is what every other section builds on",
    promptName: PROMPT_KEYS.IDEA_STATEMENT,
    primaryNodeTitle: "Your Big Idea",
    icon: "/icons/idea.svg",
    headerGradientTop: "#FFD3A8",
    headerGradientBottom: "#FFCC99",
    deliverableHeaderColor: "#FFE9D5",
    stepIconTileColor: "#fcc590",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.SUMMARY,
        title: "Summary",
        promptName: PROMPT_KEYS.SUMMARY,
        icon: "/icons/idea.svg",
        contentType: "markdown",
        gatesTaskGeneration: true,
        requiresReview: false,
        readOnly: true,
        requiredInputs: [
          STEP_IDS.IDEA,
          STEP_IDS.CUSTOMER_PROBLEM,
          STEP_IDS.COMPETITIVE,
          STEP_IDS.TAM,
          STEP_IDS.BRAND,
          STEP_IDS.GTM,
          STEP_IDS.PRODUCT_BUILD,
          STEP_IDS.TEAM,
        ],
      },
    ],
    rubricCategories: ["Problem Clarity", "Solution Approach"],
    requiredInputs: [],
    neededFacts: [],
    includeInCreateAll: true,
    // Big Idea relabels the (secondary) improve chip "Improve" so a founder's
    // first signoff teaches them Lev can rewrite the draft. Chip label only —
    // the chip text still maps to an improvement intent, so the baked agent
    // prompt is unchanged and no LEVAI_AGENT_VERSION bump is needed.
    // "Looks good" stays the primary chip — a primary must never gate.
    signoff: {
      kind: "review",
      improveLabel: "Improve",
    },
  },
  {
    id: STEP_IDS.CUSTOMER_PROBLEM,
    index: 1,
    // Display name renamed "Customer" → "Customer Discovery" (2026-07-28):
    // the bare noun read badly in impact CTAs ("Work on Customer") and agent
    // speech ("I've started Customer"). Existing blocks keep their persisted
    // title; new ones pick this up.
    label: "Customer Discovery",
    description: "Who You're Building For",
    whyItMatters: "knowing exactly who you serve and the problem you solve keeps every later decision honest",
    promptName: PROMPT_KEYS.CUSTOMER_PROBLEM,
    primaryNodeTitle: "Customer Discovery",
    icon: "/icons/customer-problem.svg",
    headerGradientTop: "#FFE299",
    headerGradientBottom: "#FAD984",
    deliverableHeaderColor: "#FFF3D0",
    stepIconTileColor: "#F4D477",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.INTERVIEW_SCRIPT,
        title: "Interview Script",
        whyItMatters: "a consistent interview script is the basis for customer discovery",
        promptName: PROMPT_KEYS.INTERVIEW_SCRIPT,
        icon: "/icons/assets/customer-problem.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.frost,
        contentType: "markdown",
        gatesTaskGeneration: true,
        neededFacts: ["icpHypothesis", "problemStatement"],
        requiredInputs: [STEP_IDS.CUSTOMER_PROBLEM],
      },
      {
        id: DELIVERABLE_IDS.COLD_OUTREACH,
        title: "Cold Outreach Template",
        whyItMatters: "a ready outreach template is what gets you into conversations with real customers",
        promptName: PROMPT_KEYS.COLD_OUTREACH,
        icon: "/icons/assets/customer-problem.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.mist,
        contentType: "markdown",
        gatesTaskGeneration: true,
        neededFacts: ["icpHypothesis", "problemStatement", "channelHypothesis"],
        requiredInputs: [STEP_IDS.CUSTOMER_PROBLEM, STEP_IDS.GTM],
      },
      {
        id: DELIVERABLE_IDS.DISCOVERY,
        title: "Customers",
        promptName: PROMPT_KEYS.PROSPECT_LIST, // reuse existing prompt for now
        icon: "/icons/assets/customer-problem.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.slate,
        contentType: "csv", // preserved for compatibility; unused for pipeline blocks
        customViewer: "pipeline-board",
        gatesTaskGeneration: false,
        generationSource: "apollo",
        // Pipeline boards are living workspaces with no approval path — they
        // must never enter the review loop (no signoff chips, no review queue
        // item, no needs-review jewel).
        requiresReview: false,
        requiredInputs: [STEP_IDS.CUSTOMER_PROBLEM],
      },
    ],
    typicallyAfter: [STEP_IDS.IDEA],
    rubricCategories: ["Customer Problem", "Pain Intensity"],
    requiredInputs: [STEP_IDS.IDEA],
    neededFacts: ["solutionDomain"],
    includeInCreateAll: true,
    signoff: {
      kind: "choice",
      candidatesKey: "icpCandidates",
      chipTemplate: "Focus on {name}",
      rewriteOnChoice: true,
      chosenKey: "chosenIcp",
    },
  },
  {
    id: STEP_IDS.COMPETITIVE,
    index: 2,
    label: "Competitive Landscape",
    description: "Who You're Competing With",
    whyItMatters: "knowing who else your customer could pick sharpens how you stand apart",
    promptName: PROMPT_KEYS.COMPETITIVE_LANDSCAPE,
    primaryNodeTitle: "Competitive Landscape",
    icon: "/icons/competitive.svg",
    headerGradientTop: "#B3F0BD",
    headerGradientBottom: "#99E8A6",
    deliverableHeaderColor: "#E5F9EA",
    stepIconTileColor: "#A2E5AF",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.COMPETITORS_CSV,
        title: "Competitors (detail)",
        promptName: PROMPT_KEYS.COMPETITORS_DATA,
        icon: "/icons/assets/competitive.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.slate,
        contentType: "csv",
        requiresReview: false,
        gatesTaskGeneration: true,
        neededFacts: ["solutionDomain", "competitorNames"],
        requiredInputs: [STEP_IDS.COMPETITIVE],
      },
    ],
    typicallyAfter: [STEP_IDS.CUSTOMER_PROBLEM],
    rubricCategories: ["Competitive Landscape", "Differentiation"],
    requiredInputs: [STEP_IDS.CUSTOMER_PROBLEM],
    neededFacts: ["solutionDomain"],
    includeInCreateAll: true,
  },
  {
    id: STEP_IDS.TAM,
    index: 3,
    label: "Market Size & Pricing",
    description: "How Big Is Your Market?",
    whyItMatters: "a credible market size and price tell you, and investors, whether this can become a real business",
    promptName: PROMPT_KEYS.TAM_ANALYSIS,
    primaryNodeTitle: "Market Size & Pricing",
    icon: "/icons/tam.svg",
    headerGradientTop: "#B3F4F0",
    headerGradientBottom: "#8FF2EC",
    deliverableHeaderColor: "#E3FBF8",
    stepIconTileColor: "#9FE9E3",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.MARKET_SIZING,
        title: "Market Sizing Worksheet",
        whyItMatters: "showing your working on market size is what makes the number believable",
        promptName: PROMPT_KEYS.MARKET_SIZING,
        icon: "/icons/assets/tam.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.tide,
        contentType: "markdown",
        gatesTaskGeneration: true,
        neededFacts: ["customerSegment", "pricingSignal", "marketTrends", "chosenIcp"],
        requiredInputs: [STEP_IDS.TAM, STEP_IDS.CUSTOMER_PROBLEM],
      },
    ],
    typicallyAfter: [STEP_IDS.CUSTOMER_PROBLEM, STEP_IDS.COMPETITIVE],
    rubricCategories: ["TAM/Trends", "Market Opportunity"],
    requiredInputs: [STEP_IDS.COMPETITIVE, STEP_IDS.CUSTOMER_PROBLEM],
    neededFacts: ["chosenIcp"],
    includeInCreateAll: true,
  },
  {
    id: STEP_IDS.BRAND,
    index: 4,
    label: "Brand Development",
    description: "How You'll Build Your Brand",
    whyItMatters: "a clear brand makes your product recognisable and easy to say yes to",
    promptName: PROMPT_KEYS.BRAND_POSITIONING,
    primaryNodeTitle: "Brand Development",
    icon: "/icons/positioning.svg",
    headerGradientTop: "#D4BDFF",
    headerGradientBottom: "#C8AAFF",
    deliverableHeaderColor: "#EEE6FF",
    stepIconTileColor: "#C5A8FB",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.BRAND_VISUALS,
        title: "Color Palette & Visual Direction",
        whyItMatters: "a settled visual direction keeps everything you make looking like one product",
        promptName: PROMPT_KEYS.BRAND_VISUALS,
        icon: "/icons/assets/positioning.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.frost,
        contentType: "markdown",
        customViewer: "brand_visuals",
        gatesTaskGeneration: true,
        neededFacts: ["brandName", "brandValues", "icpHypothesis"],
        requiredInputs: [STEP_IDS.BRAND],
      },
    ],
    typicallyAfter: [STEP_IDS.CUSTOMER_PROBLEM, STEP_IDS.COMPETITIVE],
    rubricCategories: ["Business Model", "Value Proposition"],
    requiredInputs: [STEP_IDS.COMPETITIVE, STEP_IDS.CUSTOMER_PROBLEM],
    // chosenIcp: positioning written against 2-3 candidate ICPs hedges across
    // audiences and nothing rewrites it after the founder commits (the
    // rewrite-for-icp chain only rewrites Customer Discovery) — so Brand
    // waits for the commitment like TAM/GTM do. Competitive stays ungated:
    // the landscape does not change with the wedge.
    neededFacts: ["icpHypothesis", "differentiationClaim", "solutionDomain", "chosenIcp"],
    includeInCreateAll: true,
  },
  {
    id: STEP_IDS.GTM,
    index: 5,
    label: "Go to Market",
    description: "How You'll Get Customers",
    whyItMatters: "a concrete plan for reaching customers is what turns the idea into traction",
    promptName: PROMPT_KEYS.GO_TO_MARKET,
    primaryNodeTitle: "Go to Market",
    icon: "/icons/gtm.svg",
    headerGradientTop: "#FFA8DB",
    headerGradientBottom: "#FF9ED7",
    deliverableHeaderColor: "#FFE3F4",
    stepIconTileColor: "#F98EC8",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.LANDING_PAGE,
        title: "Landing Page",
        whyItMatters: "a landing page is the fastest way to test whether the message lands with customers",
        promptName: PROMPT_KEYS.LANDING_PAGE,
        icon: "/icons/assets/gtm.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.mist,
        contentType: "html",
        customViewer: "landing-page",
        gatesTaskGeneration: true,
        neededFacts: ["brandName", "brandValues", "icpHypothesis", "solutionDomain", "chosenIcp"],
        requiredInputs: [
          STEP_IDS.IDEA,
          STEP_IDS.BRAND,
          STEP_IDS.COMPETITIVE,
          STEP_IDS.GTM,
        ],
        downloadable: false,
        copyable: false,
      },
      {
        id: DELIVERABLE_IDS.EXPERIMENTS,
        title: "Experiments Backlog",
        whyItMatters: "a backlog of experiments turns go-to-market from a guess into a plan you can run",
        promptName: PROMPT_KEYS.GTM_EXPERIMENTS,
        icon: "/icons/assets/gtm.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.mist,
        contentType: "markdown",
        gatesTaskGeneration: true,
        neededFacts: ["channelHypothesis", "icpHypothesis", "chosenIcp"],
        requiredInputs: [STEP_IDS.GTM, STEP_IDS.CUSTOMER_PROBLEM],
      },
      // LEV-865: the Deals pipeline was removed entirely. The Customers
      // pipeline (under CUSTOMER_PROBLEM) now covers discovery through to a
      // won customer/pilot.
    ],
    typicallyAfter: [STEP_IDS.CUSTOMER_PROBLEM],
    rubricCategories: ["Go-to-Market", "Customer Acquisition"],
    requiredInputs: [
      STEP_IDS.BRAND,
      STEP_IDS.CUSTOMER_PROBLEM,
      STEP_IDS.COMPETITIVE,
    ],
    neededFacts: ["icpHypothesis", "customerSegment", "revenueModel", "chosenIcp"],
    includeInCreateAll: true,
  },
  {
    id: STEP_IDS.PRODUCT_BUILD,
    index: 6,
    label: "Product Build",
    description: "What You're Building",
    whyItMatters: "writing down what to build keeps the product tied to the customer problem",
    promptName: PROMPT_KEYS.PRODUCT_SPEC,
    primaryNodeTitle: "Product Build",
    icon: "/icons/product-build.svg",
    headerGradientTop: "#D3F1FF",
    headerGradientBottom: "#B4E7FF",
    deliverableHeaderColor: "#E7F6FF",
    stepIconTileColor: "#BCE3FA",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.PRD,
        title: "Product Requirements Doc (PRD)",
        whyItMatters: "writing the requirements down keeps the build tied to what customers need",
        promptName: PROMPT_KEYS.PRD,
        icon: "/icons/assets/product-build.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.slate,
        contentType: "markdown",
        gatesTaskGeneration: true,
        neededFacts: ["productDescription", "icpHypothesis", "solutionDomain"],
        requiredInputs: [
          STEP_IDS.PRODUCT_BUILD,
          STEP_IDS.TAM,
          STEP_IDS.COMPETITIVE,
          STEP_IDS.BRAND,
          STEP_IDS.GTM,
          STEP_IDS.CUSTOMER_PROBLEM,
        ],
      },
      {
        id: DELIVERABLE_IDS.TRD,
        title: "Technical Requirements Doc (TRD)",
        whyItMatters: "a technical spec lets whoever builds it move without guessing",
        promptName: PROMPT_KEYS.TRD,
        icon: "/icons/assets/product-build.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.tide,
        contentType: "markdown",
        gatesTaskGeneration: true,
        neededFacts: ["productDescription", "techStack", "solutionDomain"],
        requiredInputs: [
          STEP_IDS.PRODUCT_BUILD,
          STEP_IDS.TAM,
          STEP_IDS.COMPETITIVE,
          STEP_IDS.BRAND,
          STEP_IDS.GTM,
          STEP_IDS.CUSTOMER_PROBLEM,
        ],
      },
    ],
    typicallyAfter: [STEP_IDS.CUSTOMER_PROBLEM, STEP_IDS.GTM],
    rubricCategories: ["Product Feasibility", "MVP Scope"],
    requiredInputs: [
      STEP_IDS.GTM,
      STEP_IDS.BRAND,
      STEP_IDS.CUSTOMER_PROBLEM,
      STEP_IDS.TAM,
      STEP_IDS.COMPETITIVE,
    ],
    neededFacts: ["icpHypothesis", "solutionDomain"],
    includeInCreateAll: true,
  },
  {
    id: STEP_IDS.TEAM,
    index: 7,
    label: "Team",
    description: "Who Is Building This",
    whyItMatters: "showing who is building this, and who you still need, is what investors weigh most",
    promptName: PROMPT_KEYS.TEAM_ANALYSIS,
    primaryNodeTitle: "Team",
    icon: "/icons/team.svg",
    headerGradientTop: "#CFF3EB",
    headerGradientBottom: "#ACF7E6",
    deliverableHeaderColor: "#E6FBF5",
    stepIconTileColor: "#B4E3D8",
    deliverableNodes: [
      {
        id: DELIVERABLE_IDS.TEAM_JOB_DESCRIPTIONS,
        title: "Job Descriptions",
        whyItMatters: "clear job descriptions are what let you hire the people the plan depends on",
        promptName: PROMPT_KEYS.TEAM_JOB_DESCRIPTIONS,
        icon: "/icons/assets/team.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.dusk,
        contentType: "markdown",
        gatesTaskGeneration: true,
        neededFacts: ["founderSkills", "hiringPriorities", "productDescription"],
        requiredInputs: [
          STEP_IDS.TEAM,
          STEP_IDS.CUSTOMER_PROBLEM,
          STEP_IDS.COMPETITIVE,
          STEP_IDS.TAM,
          STEP_IDS.PRODUCT_BUILD,
        ],
      },
      {
        id: DELIVERABLE_IDS.APPLICANTS,
        title: "Applicants",
        promptName: PROMPT_KEYS.PROSPECT_LIST, // reuse existing prompt for now
        icon: "/icons/assets/team.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.slate,
        contentType: "csv", // preserved for compatibility; unused for pipeline blocks
        customViewer: "pipeline-board",
        gatesTaskGeneration: false,
        generationSource: "apollo",
        // Pipeline boards are living workspaces with no approval path — never
        // in the review loop (see Customers above).
        requiresReview: false,
        requiredInputs: [STEP_IDS.TEAM],
      },
    ],
    typicallyAfter: [],
    rubricCategories: ["Founder-Market Fit", "Execution Readiness"],
    requiredInputs: [
      STEP_IDS.CUSTOMER_PROBLEM,
      STEP_IDS.COMPETITIVE,
      STEP_IDS.TAM,
      STEP_IDS.PRODUCT_BUILD,
    ],
    neededFacts: ["productDescription"],
    includeInCreateAll: true,
  },
  {
    id: STEP_IDS.FUNDRAISING,
    index: 9,
    label: "Fundraising",
    description: "The raise, worked end to end",
    whyItMatters: "a worked-through raise means you walk into investor conversations ready",
    // The head of the row is now the WIZARD INTRO, not a generated document
    // (LEV-1555/1557). The old Raise Brief primary was an AI-generated markdown
    // doc you approved; the follow-up that comment predicted has landed. The
    // primary now renders a designed static panel (FundraisingPrimaryPanel) that
    // frames the raise and hands off to the first deliverable — identical for
    // everyone, non-editable, and NOT generated.
    //
    // So there is deliberately NO `promptName` here: without a prompt key the
    // primary produces no document. `investor-studio` deliberately STAYS in
    // STREAMING_PRIMARY_STEP_IDS (the strategy resolver still recognises the step
    // in order to hard-refuse it — see DocumentGenerationStrategy); generation is
    // closed by that refusal together with the absent promptName.
    // `primaryNodeTitle` stays because the canvas anchors the row on the primary
    // block and `decideCreateAllStep` reads it — it is now the card title the
    // static panel sits under.
    primaryNodeTitle: "Fundraising",
    icon: "/icons/fundraising.svg",
    // Green, because it is the money row (founder's call, 2026-08-15). Also
    // does real work during the comparison window: the Fundraising row is
    // periwinkle, so two rows with the same label are still told apart at a
    // glance on the canvas. Same tonal family and saturation as the other
    // rows' pastels so it reads as part of the set, not a highlight.
    headerGradientTop: "#C4E9D2",
    headerGradientBottom: "#A6DCBB",
    deliverableHeaderColor: "#E5F5EC",
    stepIconTileColor: "#8ECFA9",
    deliverableNodes: [
      {
        // JOB 0 — decide how you are funding this. First in the row because
        // every block after it is shaped by the answer: a bootstrapper does
        // not need a target list, and telling them to build one is worse than
        // saying nothing. Short and decision-forcing, which is what belongs at
        // the head of a row (the Raise Brief document did not).
        id: DELIVERABLE_IDS.STUDIO_PATH,
        title: "Fundraising Path",
        promptName: PROMPT_KEYS.RAISE_BRIEF,
        icon: "/icons/assets/fundraising.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.mist,
        customViewer: "funding-path",
        viewerOnly: true,
        // Fundraising is a linear wizard (LEV-1555): every block is view-only,
        // driven by its panel, never edited as a document. Enforced through
        // useCanvasGraph → NodeWrappers → renderLightbox.
        readOnly: true,
        gatesTaskGeneration: false,
        requiresReview: false,
        requiredInputs: [STEP_IDS.IDEA],
      },
      {
        // JOB 1 — understand the landscape. Market + competitive funding.
        id: DELIVERABLE_IDS.STUDIO_RESEARCH,
        // Titles are prefixed "Fundraising" so a block is unambiguous on a
        // canvas that also has Competitive Landscape, Market Size and a Customers
        // board — "Research" alone could be any of them (founder, 2026-08-15).
        title: "Fundraising Research",
        promptName: PROMPT_KEYS.FUNDING_CLIMATE,
        icon: "/icons/assets/fundraising.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.mist,
        customViewer: "investor-studio",
        viewerOnly: true,
        // Fundraising is a linear wizard (LEV-1555): every block is view-only,
        // driven by its panel, never edited as a document. Enforced through
        // useCanvasGraph → NodeWrappers → renderLightbox.
        readOnly: true,
        gatesTaskGeneration: false,
        requiresReview: false,
        requiredInputs: [STEP_IDS.IDEA],
      },
      {
        // JOB 2 — the materials an investor actually sees. Deck + the hard
        // questions that sharpen it. These two belong together because they
        // improve each other: answers ground the deck, weak slides generate
        // new questions.
        id: DELIVERABLE_IDS.STUDIO_MATERIALS,
        title: "Fundraising Materials",
        promptName: PROMPT_KEYS.PITCH_DECK,
        icon: "/icons/assets/fundraising.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.mist,
        customViewer: "investor-studio",
        viewerOnly: true,
        // Fundraising is a linear wizard (LEV-1555): every block is view-only,
        // driven by its panel, never edited as a document. Enforced through
        // useCanvasGraph → NodeWrappers → renderLightbox.
        readOnly: true,
        gatesTaskGeneration: false,
        requiresReview: false,
        requiredInputs: [STEP_IDS.IDEA],
      },
      {
        // JOB 3 — who to ask, and for what. Round details, the profile of
        // investor wanted, and the researched list that follows from both.
        id: DELIVERABLE_IDS.STUDIO_LIST,
        title: "Investor Research",
        promptName: PROMPT_KEYS.PROSPECT_LIST,
        icon: "/icons/assets/fundraising.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.mist,
        customViewer: "investor-studio",
        viewerOnly: true,
        // Fundraising is a linear wizard (LEV-1555): every block is view-only,
        // driven by its panel, never edited as a document. Enforced through
        // useCanvasGraph → NodeWrappers → renderLightbox.
        readOnly: true,
        gatesTaskGeneration: false,
        requiresReview: false,
        requiredInputs: [STEP_IDS.IDEA],
      },
      {
        // JOB 4 — engage and track. Still the same PipelineBoardViewer that
        // Customers uses, resolving to the same `investor` pipeline: one board
        // seen two ways, never two boards. It renders THROUGH the studio panel
        // rather than directly, which is the only difference from the other
        // three jobs and the reason this block used to be a dead end — a
        // founder who followed the row into the tracker had no way back to the
        // target list but BACK TO CANVAS (founder, 2026-08-17). Going through
        // the panel gives it the same cross-block nav as its siblings.
        id: DELIVERABLE_IDS.STUDIO_TARGETS,
        promptName: PROMPT_KEYS.PROSPECT_LIST,
        title: "Investor Tracker",
        icon: "/icons/assets/fundraising.svg",
        previewPalette: DELIVERABLE_PREVIEW_PALETTES.slate,
        contentType: "csv",
        customViewer: "investor-studio",
        viewerOnly: true,
        // Fundraising is a linear wizard (LEV-1555): every block is view-only,
        // driven by its panel, never edited as a document. Enforced through
        // useCanvasGraph → NodeWrappers → renderLightbox.
        readOnly: true,
        gatesTaskGeneration: false,
        requiresReview: false,
        requiredInputs: [STEP_IDS.IDEA],
      },
    ],
    typicallyAfter: [],
    // Product decision (2026-09-21): raise lane opens on Customer Problem,
    // Competitive, and TAM — the three story pillars an investor pitch requires.
    // Brand/GTM/Product Build/Team are NOT gates. This list is the single
    // source: FUNDRAISING_FOUNDATION_STEPS in stageRubric.ts derives from it.
    requiredInputs: [
      STEP_IDS.CUSTOMER_PROBLEM,
      STEP_IDS.COMPETITIVE,
      STEP_IDS.TAM,
    ],
    neededFacts: ["fundingHistory", "tractionData", "revenueModel"],
    // Opt-out of Create All: the primary is a static wizard intro with no
    // generator (see the primaryNodeTitle note above), so Create All has
    // nothing to build here. `decideCreateAllStep` returns "skip" for an
    // excluded step that has no dependents — Fundraising is last in STEP_ORDER,
    // so the loop skips it cleanly instead of timing out on a primary that
    // never generates.
    includeInCreateAll: false,
  },
];

/**
 * Tile 02 of the Customer Discovery row for projects stamped
 * `customerDiscoveryV2` (D3, row-order spec). Deliberately NOT inside
 * `PRODUCT_STEPS`: every place that iterates `deliverableNodes` today
 * (Create All, `deliverableCatalog.ts`, `exhaustion.ts`, `nextStep.ts`,
 * `compassActions.ts`) must never see it, so flag-off output cannot change.
 * `findDeliverableDefinition` falls back to `CUSTOMER_DISCOVERY_V2_NODES`
 * below, so an ICP block still resolves its viewer from any caller.
 */
export const ICP_CRITERIA_NODE: DeliverableNodeDefinition = {
  id: DELIVERABLE_IDS.ICP_CRITERIA,
  title: "Ideal Customer Profile (ICP) & Criteria",
  whyItMatters:
    "knowing exactly who you're targeting keeps outreach and interviews aimed at people who actually have the problem",
  // Viewer-only tiles never resolve a prompt; this reuses an existing key, as
  // the Fundraising viewer-only tiles do, because `promptName` is required.
  promptName: PROMPT_KEYS.PROSPECT_LIST,
  icon: "/icons/assets/customer-problem.svg",
  previewPalette: DELIVERABLE_PREVIEW_PALETTES.dusk,
  customViewer: "icp-builder",
  // Dials and clusters live in ProjectIcpCriteria (icp-builder spec D4), not
  // a generated document — same shape as the Fundraising blocks above.
  // deliverableGenerationGap/requiresEnrichment need no change for this id:
  // both already key off `viewerOnly`/`customViewer`, not a per-id switch.
  viewerOnly: true,
  readOnly: true,
  requiresReview: false,
  gatesTaskGeneration: false,
  requiredInputs: [STEP_IDS.CUSTOMER_PROBLEM],
};

const CUSTOMER_PROBLEM_V1_DELIVERABLE_NODES: DeliverableNodeDefinition[] =
  PRODUCT_STEPS.find((step) => step.id === STEP_IDS.CUSTOMER_PROBLEM)?.deliverableNodes ?? [];

/** Throws rather than silently dropping a tile if a v1 id ever gets renamed out from under this list. */
function requireCustomerProblemV1Node(id: DeliverableId): DeliverableNodeDefinition {
  const node = CUSTOMER_PROBLEM_V1_DELIVERABLE_NODES.find((n) => n.id === id);
  if (!node) {
    throw new Error(`Customer Discovery v1 deliverable node not found: ${id}`);
  }
  return node;
}

/**
 * The Customer Discovery row for v2 projects (D3). Entries for ids that
 * already exist in v1 are SPREAD COPIES of the v1 definitions, not new
 * objects — a later edit to a v1 node's generation fields, requiredInputs,
 * neededFacts, etc. reaches v2 projects for free. Only `title` diverges here.
 */
export const CUSTOMER_DISCOVERY_V2_NODES: DeliverableNodeDefinition[] = [
  ICP_CRITERIA_NODE,
  { ...requireCustomerProblemV1Node(DELIVERABLE_IDS.DISCOVERY), title: "Customer Targets" },
  { ...requireCustomerProblemV1Node(DELIVERABLE_IDS.COLD_OUTREACH), title: "Outreach" },
  { ...requireCustomerProblemV1Node(DELIVERABLE_IDS.INTERVIEW_SCRIPT), title: "Interview Guide" },
];

/** Non-throwing lookup — returns undefined for unknown step ids. */
function findStepDefinition(stepId: StepId): Step | undefined {
  return PRODUCT_STEPS.find((candidate) => candidate.id === stepId);
}

function getStepDefinition(stepId: StepId): Step {
  const step = findStepDefinition(stepId);
  if (!step) {
    throw new Error(`Unknown step id: ${stepId}`);
  }

  return step;
}

export function getStepIndex(stepId: StepId): number {
  return getStepDefinition(stepId).index;
}

/** Row-version options every row-aware helper below takes. Omitting it is v1 behavior, byte for byte. */
export type RowVersionOptions = { customerDiscoveryV2?: boolean };

/**
 * The ordered deliverable list for a step, row-version aware (row-order spec
 * D2/D3). Only `CUSTOMER_PROBLEM` has a v2 list; every other step ignores the
 * option. Canvas ordering/layout callers should go through this rather than
 * `step.deliverableNodes` directly so a v2 project reorders for free.
 */
export function getStepDeliverableNodes(
  stepId: StepId,
  opts?: RowVersionOptions,
): DeliverableNodeDefinition[] {
  if (opts?.customerDiscoveryV2 && stepId === STEP_IDS.CUSTOMER_PROBLEM) {
    return CUSTOMER_DISCOVERY_V2_NODES;
  }
  return getStepDefinition(stepId).deliverableNodes ?? [];
}

export function getDeliverableIndex(
  stepId: StepId,
  deliverableId: DeliverableId,
  opts?: RowVersionOptions,
): number {
  const nodes =
    opts?.customerDiscoveryV2 && stepId === STEP_IDS.CUSTOMER_PROBLEM
      ? CUSTOMER_DISCOVERY_V2_NODES
      : getStepDefinition(stepId).deliverableNodes;
  const idx = nodes?.findIndex((n) => n.id === deliverableId) ?? 0;
  return idx;
}

export function getPrimaryTitle(stepId: StepId): string {
  return getStepDefinition(stepId).primaryNodeTitle ?? "";
}

export function getDeliverableTitle(
  stepId: StepId,
  deliverableId: DeliverableId,
  opts?: RowVersionOptions,
): string {
  const nodes =
    opts?.customerDiscoveryV2 && stepId === STEP_IDS.CUSTOMER_PROBLEM
      ? CUSTOMER_DISCOVERY_V2_NODES
      : getStepDefinition(stepId).deliverableNodes;
  const deliverableTitle = nodes?.find(
    (deliverable) => deliverable.id === deliverableId,
  )?.title;

  if (!deliverableTitle) {
    throw new Error(`Unknown deliverable id ${deliverableId} for step ${stepId}.`);
  }

  return deliverableTitle;
}

/**
 * Non-throwing deliverable lookup — undefined for unknown step/deliverable
 * ids. Checks v1 first, then, for Customer Problem only, falls back to
 * `CUSTOMER_DISCOVERY_V2_NODES` by id (no `opts` param): the ICP tile has no
 * v1 home, so any caller that doesn't yet know about row versions still
 * resolves an existing ICP block. The fallback MUST stay scoped to Customer
 * Problem, or another step would resolve the v2 copies of Customer Problem's
 * ids. It also means this is a classification lookup, not a creation
 * allow-list: block-creation paths validate against
 * `getDeliverableDefinitions(stepId)` so an unstamped project can't gain an
 * ICP tile. The three ids v1 and v2 share resolve to the v1 definition here;
 * callers wanting the v2 title go through `getDeliverableTitle` with
 * `opts.customerDiscoveryV2`.
 */
export function findDeliverableDefinition(
  stepId: StepId,
  deliverableId: string,
): DeliverableNodeDefinition | undefined {
  const v1Node = findStepDefinition(stepId)?.deliverableNodes?.find(
    (node) => node.id === deliverableId,
  );
  if (v1Node) return v1Node;
  if (stepId !== STEP_IDS.CUSTOMER_PROBLEM) return undefined;
  return CUSTOMER_DISCOVERY_V2_NODES.find((node) => node.id === deliverableId);
}

/**
 * Whether a DRAFT of the given deliverable needs founder review + approval.
 * Reads `requiresReview` off the deliverable definition; absent means true.
 * Unknown ids default to true so a review is never silently skipped.
 */
export function deliverableRequiresReview(
  stepId: StepId,
  deliverableId: string,
): boolean {
  return findDeliverableDefinition(stepId, deliverableId)?.requiresReview !== false;
}

/**
 * The founder-voice "why it matters" sentence for a step or one of its
 * deliverables. A deliverable's own sentence wins when set; otherwise the
 * parent step's sentence is used. Unknown ids fall back to the empty string so
 * the NEXT ACTION renderer never crashes on a stale id.
 */
export function whyItMattersFor(
  stepId: StepId,
  deliverableId?: DeliverableId,
): string {
  const step = findStepDefinition(stepId);
  if (deliverableId) {
    const deliverable = findDeliverableDefinition(stepId, deliverableId);
    if (deliverable?.whyItMatters) return deliverable.whyItMatters;
  }
  return step?.whyItMatters ?? "";
}

/**
 * Whether the canvas/lightbox renders the given deliverable view-only.
 * Reads `readOnly` off the deliverable definition; absent means editable.
 */
export function isDeliverableReadOnly(
  stepId: StepId,
  deliverableId: string,
): boolean {
  return findDeliverableDefinition(stepId, deliverableId)?.readOnly === true;
}

/**
 * Why this deliverable has no document generation to run, or null when it
 * does. THE single definition of "nothing to generate" — the strategy
 * resolver, the enqueue chokepoint, and the approve-primary batch filter all
 * consume this instead of re-deriving it, so the client's "nothing to
 * generate" verdict and the server's refusal to enqueue cannot diverge (one
 * updated without the other means either a doomed job or a card spinning on a
 * job that was never started).
 *
 * - Apollo-populated deliverables generate nothing themselves but DO have
 *   work behind them (the Apollo job), so they report null here.
 * - Pipeline boards are lists of people, not documents — no generator exists
 *   by construction. (Investor Targets additionally dispatches its population
 *   job; that side effect lives at the enqueue chokepoint, not here.)
 * - `generatorPending` marks a deliverable registered ahead of its generator
 *   (see the field's doc above).
 */
export function deliverableGenerationGap(
  def: DeliverableNodeDefinition,
): "pipeline-board" | "generator-pending" | "viewer-only" | null {
  if (def.generationSource === "apollo") return null;
  if (def.customViewer === "pipeline-board") return "pipeline-board";
  // The EXPLICIT marker, not inferred from customViewer alone: a deliverable
  // can pair a custom viewer with a real generator (Landing Page renders
  // through its viewer but is generated from `promptName`). Inferring
  // "viewer-only" from customViewer classified it as ungeneratable —
  // approval-batch generation skipped it and left an empty shell instead.
  if (def.viewerOnly) return "viewer-only";
  if (def.generatorPending) return "generator-pending";
  return null;
}

/**
 * Whether generating this deliverable spends enrichment credits — Apollo
 * people-discovery or Deepline (LEV-1647). True for apollo-sourced deliverables
 * and pipeline boards. A sentinel-owned anonymous project may expire unclaimed
 * (14-day TTL), so the no-enrichment rule refuses these for it: the cards
 * render as locked ghosts and the blocks appear only after claim. Kept beside
 * `deliverableGenerationGap` because both classify a deliverable by the same
 * fields; adding a new enrichment source is an "ask first" change.
 */
export function requiresEnrichment(def: DeliverableNodeDefinition): boolean {
  return def.generationSource === "apollo" || def.customViewer === "pipeline-board";
}

export const FUNDRAISING_DELIVERABLE_IDS: ReadonlySet<string> = new Set(
  PRODUCT_STEPS.find((step) => step.id === STEP_IDS.FUNDRAISING)
    ?.deliverableNodes?.map((node) => node.id) ?? [],
);

export function isFundraisingBlock(block: {
  stepId?: string | null;
  deliverableId?: string | null;
}): boolean {
  if (block.deliverableId && FUNDRAISING_DELIVERABLE_IDS.has(block.deliverableId)) {
    return true;
  }
  return block.stepId === STEP_IDS.FUNDRAISING;
}

/**
 * The Fundraising WIZARD INTRO — the row's primary block.
 *
 * True only for the Fundraising step's primary (no deliverableId): the block
 * that renders the static FundraisingPrimaryPanel instead of a generated
 * document. A deliverable of the same step is NOT the primary, so it is
 * excluded. Used by the canvas render layer (useCanvasGraph / NodeWrappers) to
 * swap in the panel and mark the primary read-only.
 */
export function isFundraisingPrimaryBlock(block: {
  stepId?: string | null;
  deliverableId?: string | null;
}): boolean {
  return block.stepId === STEP_IDS.FUNDRAISING && !block.deliverableId;
}

export type PostApprovalNavTarget = {
  stepId: StepId;
  deliverableId: string;
};

export const POST_APPROVAL_NAV_OVERRIDES: Partial<
  Record<StepId, PostApprovalNavTarget>
> = {
  [STEP_IDS.CUSTOMER_PROBLEM]: {
    stepId: STEP_IDS.CUSTOMER_PROBLEM,
    deliverableId: DELIVERABLE_IDS.DISCOVERY,
  },
};

/**
 * Where approving tile 01 (Customer Problem) jumps to (row-order spec,
 * Behavior 5). v2 opens the ICP tile instead of `POST_APPROVAL_NAV_OVERRIDES`'
 * v1 target (Customers/`discovery`) — v1 projects and every other step are
 * unaffected, since the override map is untouched.
 */
export function getPostApprovalNavTarget(
  stepId: StepId,
  opts?: RowVersionOptions,
): PostApprovalNavTarget | undefined {
  if (opts?.customerDiscoveryV2 && stepId === STEP_IDS.CUSTOMER_PROBLEM) {
    return {
      stepId: STEP_IDS.CUSTOMER_PROBLEM,
      deliverableId: DELIVERABLE_IDS.ICP_CRITERIA,
    };
  }
  return POST_APPROVAL_NAV_OVERRIDES[stepId];
}

export const STEP_ORDER = [
  STEP_IDS.IDEA,
  STEP_IDS.CUSTOMER_PROBLEM,
  STEP_IDS.COMPETITIVE,
  STEP_IDS.TAM,
  STEP_IDS.BRAND,
  STEP_IDS.GTM,
  STEP_IDS.PRODUCT_BUILD,
  STEP_IDS.TEAM,
  STEP_IDS.FUNDRAISING,
] as const;

// Helper to get step definition by ID, exporting for use in context resolution
export { getStepDefinition };
